import {IconButton} from '@dropbox/dig-components/buttons';
import {Toggle} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ClockLine, CommentLine, LinkLine, MoreHorizontalLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {pulseUserAtom} from 'atoms/auth';
import {snackbarAtom} from 'atoms/snackbar';
import {EditorDocStatus, EditorDocTemplateType} from 'client';
import {AppHelpMenuDropdown} from 'components/AppHelpMenuSegments';
import {DocumentTopNav, DocumentTopNavPlaceholder} from 'components/DSYS/document/TopNav';
import {PresenceFacepile} from 'components/DSYS/PresenceFacepile';
import {NotificationButton} from 'components/notifications/NotificationButton';
import {extractIdFromSlug, isSuperUser} from 'helpers/utils';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {type MouseEvent, useCallback, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router';

import {usePlanning, usePlanningDocService} from './hooks';
import {PlanningDrawerType} from './PlanningDoc';
import {
  convertPlanningDocToDocument,
  DEFAULT_NARRATIVE_STATUS_OPTIONS,
  DEFAULT_SWITCHBOARD_STATUS_OPTIONS,
  getPlanningDocStatus,
} from './utils';

export const PlanningDocNav = ({
  activeDrawer,
  onDrawerToggle,
  showTemplateModeToggle,
  isTemplateMode,
  onTemplateModeChange,
}: {
  activeDrawer: PlanningDrawerType;
  onDrawerToggle: (drawerType: PlanningDrawerType) => void;
  showTemplateModeToggle: boolean;
  isTemplateMode: boolean;
  onTemplateModeChange: (templateMode: boolean) => void;
}) => {
  const params = useParams() as {id: string};
  const pulseUser = useAtomValue(pulseUserAtom);
  const planningId = extractIdFromSlug(params.id);
  const {planning} = usePlanning(planningId);
  const {updatePlanningDoc} = usePlanningDocService();

  const handleStatusChange = useCallback(
    (status: EditorDocStatus) => {
      if (!planning?.id) return;
      updatePlanningDoc({planningId: planning.id, update: {status}});
    },
    [planning?.id, updatePlanningDoc]
  );

  const documentId = planning?.document?.id;
  if (!documentId) {
    return null;
  }

  const doc = convertPlanningDocToDocument(planning);
  const canViewVersionHistory = isSuperUser(pulseUser) || doc?.owner.ldap === pulseUser?.ldap;
  return (
    <>
      <DocumentTopNav
        doc={doc}
        statusMenu={{
          menuStatuses:
            doc?.statusOptions ??
            (planning?.template_type === EditorDocTemplateType.NARRATIVE
              ? DEFAULT_NARRATIVE_STATUS_OPTIONS
              : DEFAULT_SWITCHBOARD_STATUS_OPTIONS),
          onSelectStatus: (status) => {
            analyticsLogger().logEvent('WORKSTREAM_SWITCHBOARD_PHASE_SELECTED', {
              source: 'planning_doc_nav',
              phase: status,
              planning_id: planning.id,
              ...(planning.template_type != null ? {template_type: planning.template_type} : {}),
            });
            handleStatusChange(getPlanningDocStatus(status));
          },
        }}
        navMenu={
          <PlanningDocNavMenu
            showVersionButton={canViewVersionHistory}
            activeDrawer={activeDrawer}
            onDrawerToggle={onDrawerToggle}
            showTemplateModeToggle={showTemplateModeToggle}
            isTemplateMode={isTemplateMode}
            onTemplateModeChange={onTemplateModeChange}
          />
        }
      />
    </>
  );
};

export const PlanningDocNavPlaceholder = () => {
  const params = useParams() as {id: string};
  const planningId = extractIdFromSlug(params.id);
  const {planning, isError} = usePlanning(planningId);
  return (
    <DocumentTopNavPlaceholder
      doc={convertPlanningDocToDocument(planning)}
      isError={isError && !planning}
    />
  );
};

const PlanningDocNavMenu = ({
  showVersionButton,
  onDrawerToggle,
  showTemplateModeToggle,
  isTemplateMode,
  onTemplateModeChange,
}: {
  showVersionButton: boolean;
  activeDrawer: PlanningDrawerType;
  onDrawerToggle: (drawerType: PlanningDrawerType) => void;
  showTemplateModeToggle: boolean;
  isTemplateMode: boolean;
  onTemplateModeChange: (templateMode: boolean) => void;
}) => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const setSnackbar = useSetAtom(snackbarAtom);
  const [copyLinkTooltipTitle, setCopyLinkTooltipTitle] = useState(() => t('copy_link'));
  const copyLinkTooltipResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyLinkTooltipResetRef.current) {
        clearTimeout(copyLinkTooltipResetRef.current);
      }
    };
  }, []);

  const handleCopyPlanningDocUrl = useCallback(async () => {
    analyticsLogger().logEvent('PLANNING_DOC_NAV_COPY_LINK_CLICKED', {source: 'planning_doc_nav'});
    if (copyLinkTooltipResetRef.current) {
      clearTimeout(copyLinkTooltipResetRef.current);
      copyLinkTooltipResetRef.current = null;
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyLinkTooltipTitle(t('copied'));
      copyLinkTooltipResetRef.current = setTimeout(() => {
        setCopyLinkTooltipTitle(t('copy_link'));
        copyLinkTooltipResetRef.current = null;
      }, 2500);
    } catch {
      setSnackbar({text: t('error_copying_link')});
    }
  }, [setSnackbar]);

  return (
    <>
      <Box>
        <PresenceFacepile shouldApplyInactiveGracePeriod />
      </Box>
      <Tooltip title={copyLinkTooltipTitle}>
        <Box marginLeft="2" style={{marginRight: -4}}>
          <IconButton
            variant="transparent"
            onMouseDown={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
            }}
            onClick={() => void handleCopyPlanningDocUrl()}
            aria-label="icon button"
          >
            <UIIcon src={LinkLine} className={atoms({color: 'Text Subtle'})} />
          </IconButton>
        </Box>
      </Tooltip>

      <Menu.Wrapper shouldPropagateClickOutsideMouseEvents isPortaled={false}>
        {({getContentProps, getTriggerProps}) => (
          <>
            <Tooltip title={t('more_title')}>
              <Box>
                <IconButton
                  {...getTriggerProps()}
                  variant="transparent"
                  onMouseDown={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                  }}
                  aria-label="icon button"
                >
                  <UIIcon src={MoreHorizontalLine} className={atoms({color: 'Text Subtle'})} />
                </IconButton>
              </Box>
            </Tooltip>

            <Menu.Content
              {...getContentProps()}
              minWidth="220px"
              placement="bottom-end"
              onContentOpened={() => {
                analyticsLogger().logEvent('PLANNING_DOC_NAV_OVERFLOW_OPENED', {
                  source: 'planning_doc_nav',
                });
              }}
            >
              <Menu.Segment>
                {showTemplateModeToggle ? (
                  <Menu.Row
                    onClick={() => onTemplateModeChange(!isTemplateMode)}
                    withRightAccessory={
                      <Box
                        onClick={(event: MouseEvent<HTMLDivElement>) => {
                          event.stopPropagation();
                        }}
                      >
                        <Toggle
                          isToggled={isTemplateMode}
                          checked={isTemplateMode}
                          onChange={() => onTemplateModeChange(!isTemplateMode)}
                        />
                      </Box>
                    }
                    interactive
                  >
                    {t('template_mode')}
                  </Menu.Row>
                ) : null}
                <Menu.ActionItem
                  withLeftAccessory={<UIIcon src={CommentLine} />}
                  onClick={() => {
                    analyticsLogger().logEvent('PLANNING_DOC_NAV_MENU_ITEM_CLICKED', {
                      source: 'planning_doc_nav',
                      item: 'comments',
                    });
                    onDrawerToggle('comments');
                  }}
                >
                  {t('comments_history')}
                </Menu.ActionItem>
                {showVersionButton && (
                  <Menu.ActionItem
                    withLeftAccessory={<UIIcon src={ClockLine} />}
                    onClick={() => {
                      analyticsLogger().logEvent('PLANNING_DOC_NAV_MENU_ITEM_CLICKED', {
                        source: 'planning_doc_nav',
                        item: 'versions',
                      });
                      onDrawerToggle('versions');
                    }}
                  >
                    {t('versions_history')}
                  </Menu.ActionItem>
                )}
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>

      <Box
        borderLeft="Solid"
        borderWidth="1"
        borderColor="Border Subtle"
        paddingLeft={isSuperUser(pulseUser) ? '2' : '6'}
        marginLeft="4"
        style={{height: 30}}
      />
      {isSuperUser(pulseUser) && <AppHelpMenuDropdown />}
      <NotificationButton />
    </>
  );
};
